/**
 * playerColor - the visitor's chosen avatar color.
 *
 * Picked on the arrival screen (swatch row), persisted in plain localStorage
 * `playerColor` (identity data like `playerName`, deliberately outside
 * settingsStore so "Reset all settings" keeps it), and read by
 * multiplayer.js when setting the Photon `color` actor property at join.
 * A curated random fallback replaces the old fully-random color so avatars
 * without a stored choice still land on a readable palette color.
 */

const STORAGE_KEY = 'playerColor';

/**
 * Curated palette: the three brand colors plus five distinct, readable hues.
 * Swatches on the arrival screen render exactly these.
 * @type {ReadonlyArray<string>}
 */
export const AVATAR_COLORS = Object.freeze([
  '#9900e6', // brand purple
  '#ff4dc4', // brand pink
  '#ff8833', // brand orange
  '#2f8f4e', // green
  '#1d7fd6', // blue
  '#e6b800', // yellow
  '#d84a3f', // red
  '#4ac2c9', // teal
]);

/**
 * @param {unknown} value
 * @returns {value is string}
 */
export function isValidPlayerColor(value) {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
}

/** @returns {string|null} the stored choice, or null when unset/invalid */
export function getPlayerColor() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isValidPlayerColor(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Persist a color choice. Invalid values and private-mode storage failures
 * are swallowed - the color degrades to the random fallback.
 * @param {string} color - '#rrggbb'
 */
export function setPlayerColor(color) {
  if (!isValidPlayerColor(color)) return;
  try {
    localStorage.setItem(STORAGE_KEY, color);
  } catch {
    /* private mode - choice lasts this page load only */
  }
}

/** @returns {string} a random palette color (join-time fallback) */
export function randomAvatarColor() {
  return AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)];
}
